import type { MelodyPitchId } from './melodiaBuilderConfig';

export type MelodyKeyId = 'C-major' | 'G-major' | 'F-major' | 'A-minor' | 'pentatonic';

export type Subdivision = 1 | 2 | 4;

export type BuildTool = 'pencil' | 'lasso';

export type ChordDegreeId = 'I' | 'ii' | 'iii' | 'IV' | 'V' | 'vi';

export interface MelodyKeyDef {
  id: MelodyKeyId;
  label: string;
  pitches: MelodyPitchId[];
  chordDegrees: Partial<Record<ChordDegreeId, MelodyPitchId[]>>;
}

export const SUBDIVISION_OPTIONS: { value: Subdivision; label: string; hint: string }[] = [
  { value: 1, label: 'Negras', hint: '1 celda = 1 tiempo' },
  { value: 2, label: 'Corcheas', hint: '2 celdas por tiempo' },
  { value: 4, label: 'Semicorcheas', hint: '4 celdas por tiempo' },
];

export const MELODY_KEYS: MelodyKeyDef[] = [
  {
    id: 'C-major',
    label: 'Do mayor',
    pitches: ['Do4', 'Re4', 'Mi4', 'Fa4', 'Sol4', 'La4', 'Si4', 'Do5'],
    chordDegrees: {
      I: ['Do4', 'Mi4', 'Sol4'],
      ii: ['Re4', 'Fa4', 'La4'],
      iii: ['Mi4', 'Sol4', 'Si4'],
      IV: ['Fa4', 'La4', 'Do5'],
      V: ['Sol4', 'Si4', 'Re4'],
      vi: ['La4', 'Do5', 'Mi4'],
    },
  },
  {
    id: 'G-major',
    label: 'Sol mayor',
    pitches: ['Do4', 'Re4', 'Mi4', 'Fa4', 'Sol4', 'La4', 'Si4', 'Do5'],
    chordDegrees: {
      I: ['Sol4', 'Si4', 'Re4'],
      IV: ['Do4', 'Mi4', 'Sol4'],
      V: ['Re4', 'Fa4', 'La4'],
      vi: ['Mi4', 'Sol4', 'Si4'],
    },
  },
  {
    id: 'F-major',
    label: 'Fa mayor',
    pitches: ['Do4', 'Re4', 'Mi4', 'Fa4', 'Sol4', 'La4', 'Si4', 'Do5'],
    chordDegrees: {
      I: ['Fa4', 'La4', 'Do5'],
      IV: ['Si4', 'Re4', 'Fa4'],
      V: ['Do4', 'Mi4', 'Sol4'],
      vi: ['La4', 'Do5', 'Mi4'],
    },
  },
  {
    id: 'A-minor',
    label: 'La menor',
    pitches: ['Do4', 'Re4', 'Mi4', 'Fa4', 'Sol4', 'La4', 'Si4', 'Do5'],
    chordDegrees: {
      I: ['La4', 'Do5', 'Mi4'],
      ii: ['Si4', 'Re4', 'Fa4'],
      IV: ['Fa4', 'La4', 'Do5'],
      V: ['Mi4', 'Sol4', 'Si4'],
      vi: ['Do4', 'Mi4', 'Sol4'],
    },
  },
  {
    id: 'pentatonic',
    label: 'Pentatónica Do',
    pitches: ['Do4', 'Re4', 'Mi4', 'Sol4', 'La4', 'Do5'],
    chordDegrees: {
      I: ['Do4', 'Mi4', 'Sol4'],
      IV: ['La4', 'Do5', 'Mi4'],
      V: ['Sol4', 'La4', 'Do5'],
    },
  },
];

export const CHORD_DEGREE_BUTTONS: ChordDegreeId[] = ['I', 'ii', 'iii', 'IV', 'V', 'vi'];

export function keyDef(id: MelodyKeyId): MelodyKeyDef {
  return MELODY_KEYS.find((k) => k.id === id) ?? MELODY_KEYS[0]!;
}

export function pitchesForKey(id: MelodyKeyId): MelodyPitchId[] {
  return keyDef(id).pitches;
}

export function chordPitches(keyId: MelodyKeyId, degree: ChordDegreeId): MelodyPitchId[] {
  return keyDef(keyId).chordDegrees[degree] ?? [];
}
